import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { NavLink, useHistory } from 'react-router-dom';
import get from 'lodash.get';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Forbidden,
  IconCheckmark,
  IconDisabled,
  List,
  Page,
  SearchForm,
} from 'shared/components';
import { displayValue, formatDate, hasPermission } from 'shared/helpers';
import { fetchLicenseUsers } from './actions';
import './styles.scss';

const PAGE_SIZE = 20;

const LicenseUsers = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const permissionsList = useSelector((state) =>
    get(state, 'user.details.permissions'),
  );
  const activeAccountId = useSelector((state) =>
    get(state, 'account.activeAccountId'),
  );
  const canManageLicenseUsers = hasPermission(
    permissionsList,
    'manage_license_users',
  );

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(0);
  const [retryToken, setRetryToken] = useState(0);

  const [activeQuery, setActiveQuery] = useState('');
  const [searchBy, setSearchBy] = useState('true_email__icontains');
  const [currentSort, setCurrentSort] = useState([
    { desc: true, id: 'created_at' },
  ]);

  useEffect(() => {
    setPage(0);
  }, [activeAccountId]);

  useEffect(() => {
    if (!canManageLicenseUsers) {
      return;
    }
    setLoading(true);
    setError(false);

    const filterPayload = {
      ...(activeAccountId
        ? { license__order__customer_account: activeAccountId }
        : {}),
    };

    fetchLicenseUsers({
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
      query: activeQuery,
      queryType: searchBy,
      orderBy: currentSort,
      filters: filterPayload,
    })
      .then((res) => {
        const payload = get(res, 'data');
        setUsers(get(payload, 'results', []));
        setCount(get(payload, 'count', 0));
        setLoading(false);
      })
      .catch(() => {
        setError(true);
        setUsers([]);
        setCount(0);
        setLoading(false);
      });
  }, [
    canManageLicenseUsers,
    activeAccountId,
    activeQuery,
    currentSort,
    page,
    retryToken,
    searchBy,
  ]);

  const pages = Math.ceil(count / PAGE_SIZE);

  const searchOptions = useMemo(
    () => [
      {
        label: t('Search by email contains'),
        value: 'true_email__icontains',
      },
      {
        label: t('Search by email exact'),
        value: 'true_email__iexact',
      },
      {
        label: t('Search by first name'),
        value: 'first_name__icontains',
      },
      {
        label: t('Search by last name'),
        value: 'last_name__icontains',
      },
      {
        label: t('Search by phone'),
        value: 'phone__icontains',
      },
    ],
    [t],
  );

  const handleSearchSubmit = (val) => {
    setActiveQuery(val);
    setPage(0);
  };

  const handleClear = () => {
    setActiveQuery('');
    setPage(0);
  };

  const handleRetry = () => setRetryToken((token) => token + 1);
  const handleRowClick = (rowData) => {
    const userId = get(rowData, 'original.id');
    if (userId) {
      history.push(`/license_users/${userId}`);
    }
  };

  if (!canManageLicenseUsers) {
    return (
      <Page>
        <Forbidden
          text={t('Missing permission')}
          description={t('You do not have permission to perform this action.')}
        />
      </Page>
    );
  }

  return (
    <Page
      title={t('License users')}
      breadcrumb={<NavLink to="/">{t('Home')}</NavLink>}
    >
      <div className="LicenseUsersList">
        <div className="list-header">
          <SearchForm
            options={searchOptions}
            selectedValue={searchBy}
            handleSearchTypeChange={(newSearchType) =>
              setSearchBy(newSearchType)
            }
            handleSearchSubmit={handleSearchSubmit}
            activeQuery={activeQuery}
            handleClear={handleClear}
            disabled={loading}
          />
          <div />
        </div>
        {error && (
          <div className="LicenseUsersList-error">
            <p>{t('Unable to load license users')}</p>
            <Button theme="info" size="sm" onClick={handleRetry}>
              {t('Retry')}
            </Button>
          </div>
        )}
        <List
          data={users || []}
          columns={[
            {
              accessor: 'created_at',
              Header: t('Created on'),
              Cell: (cellInfo) => formatDate(cellInfo.value),
              width: 160,
            },
            {
              accessor: 'true_email',
              Header: t('Email'),
              Cell: (cellData) => displayValue(cellData.value),
            },
            {
              accessor: 'first_name',
              Header: t('First Name'),
              Cell: (cellData) => displayValue(cellData.value),
            },
            {
              accessor: 'last_name',
              Header: t('Last Name'),
              Cell: (cellData) => displayValue(cellData.value),
            },
            {
              accessor: 'phone_number',
              Header: t('Phone number'),
              Cell: (cellData) => displayValue(cellData.value),
            },
            {
              accessor: 'total_licenses',
              Header: t('Total licenses assigned'),
              className: 'text-center',
              headerClassName: 'text-center',
              Cell: (cellData) => displayValue(cellData.value, 0),
              width: 120,
            },
            {
              accessor: 'is_active',
              Header: t('Is Active'),
              headerClassName: 'text-center',
              className: 'text-center',
              Cell: (cellData) =>
                cellData.value ? (
                  <IconCheckmark color="#10ac84" height="14px" />
                ) : (
                  <IconDisabled color="#aaa" height="14px" />
                ),
              width: 90,
            },
          ]}
          page={page}
          pages={pages}
          loading={loading}
          manual
          minRows={users.length || 10}
          showPagination={pages > 1}
          onPageChange={(newPage) => setPage(newPage)}
          onSortedChange={(newSorted) => setCurrentSort(newSorted)}
          defaultSorted={currentSort}
          clickable
          handleClick={handleRowClick}
        />
      </div>
    </Page>
  );
};

export default LicenseUsers;
